/**
 * EffectRenderer — ゲームニクス視覚エフェクトのオーバーレイ描画
 *
 * GamenicsManagerが発行する `gamenics:effect` CustomEvent を購読し、
 * パーティクル（星、紙吹雪等）を画面全体にオーバーレイ描画する。
 *
 * フェイルセーフ: エフェクト描画に失敗してもアプリの動作は阻害しない。
 *
 * @see services/gamenics.ts showEffect()
 */

import React, { useEffect, useRef, useState } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  rotation: number;
  velocity: { x: number; y: number };
  life: number;
  maxLife: number;
}

interface EffectPayload {
  type: string;        // 'confetti' | 'stars' | 'slot_machine' etc.
  duration: number;    // ms
  count: number;
  size: number;
  colors: string[];
  scale: number;
}

export const EffectRenderer: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animFrameRef = useRef<number>(0);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const handleEffect = (e: Event) => {
      try {
        const detail = (e as CustomEvent<EffectPayload>).detail;
        spawnParticles(detail);
      } catch (err) {
        console.warn('[EffectRenderer] Effect spawn failed:', err);
      }
    };

    window.addEventListener('gamenics:effect', handleEffect);
    return () => window.removeEventListener('gamenics:effect', handleEffect);
  }, []);

  const spawnParticles = (payload: EffectPayload) => {
    const count = payload.count || 30;
    const colors = payload.colors || ['#FFD700', '#FF6B6B'];
    const maxLife = (payload.duration || 2000) / 16; // frames at ~60fps

    const newParticles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: Date.now() + i,
        x: Math.random() * window.innerWidth,
        y: -20 - Math.random() * 40,
        size: (4 + Math.random() * 8) * (payload.size || 1),
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        velocity: {
          x: (Math.random() - 0.5) * 6,
          y: 2 + Math.random() * 4,
        },
        life: 0,
        maxLife,
      });
    }

    particlesRef.current = [...particlesRef.current, ...newParticles];

    if (!isActive) {
      setIsActive(true);
      startAnimation();
    }
  };

  const startAnimation = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const animate = () => {
      const particles = particlesRef.current;
      if (particles.length === 0) {
        setIsActive(false);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesRef.current = particles
        .map((p) => ({
          ...p,
          x: p.x + p.velocity.x,
          y: p.y + p.velocity.y,
          rotation: p.rotation + 3,
          life: p.life + 1,
          velocity: {
            x: p.velocity.x * 0.99,
            y: p.velocity.y + 0.08, // gravity
          },
        }))
        .filter((p) => p.life < p.maxLife && p.y < canvas.height + 50);

      for (const p of particlesRef.current) {
        const alpha = 1 - p.life / p.maxLife;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[9999]"
      style={{ display: isActive ? 'block' : 'none' }}
    />
  );
};
